import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clientOkta } from "../clients";
import { fetchCountryList } from "../listCountries";
import { formatUserName } from "../../utils/extractUserName";

// Provides React Query hooks to fetch authenticated user details (useAppMe) and country list (useCountryList) with caching and error handling.

/**
 * Fetches the authenticated user's information from the API.
 *
 * @param {Object} params - The parameters for the fetch operation.
 * @param {Object} [params.query={}] - Optional query parameters to include in the request.
 * @returns {Promise<any>} A promise that resolves to the user data response.
 * @throws {Error} Throws an error if the request fails (e.g., 401 Unauthorized, 500 Internal Server Error).
 */
const normalizeRole = (role: string) => {
  const r = (role || "").toLowerCase();
  if (r === "marketer") return "Marketer";
  if (r === "marketer_admin") return "Marketer_Admin";
  if (r === "admin") return "Global_Admin";
  return role;
};

const parseCsvParam = (value: string | null) =>
  (value || "")
    .split(",")
    .map((s) => decodeURIComponent(s).trim())
    .filter(Boolean);

const fetchAppMe = async ({ query = {} }) => {
  try {
    const data = await clientOkta("/api/me", query);
    const userName = formatUserName(data?.data?.user_id);
    const next = { ...data, data: { ...data.data, user_name: userName } };

    const params = new URLSearchParams(window.location.search);
    const simRoleRaw = params.get("simRole") || "";
    const simTagRaw = params.get("simTag") || "";
    const simCountriesRaw = params.get("simCountries") || "";

    const simRole = simRoleRaw.toLowerCase();
    const simTags = parseCsvParam(simTagRaw).map((t) => t.toUpperCase());
    const simCountries = parseCsvParam(simCountriesRaw).map((c) => c.toLowerCase());

    const originalGroups: string[] = next?.data?.ccdp_groups || [];
    const isAllowedToSimulate =
      originalGroups.includes("RC_Okta_CDP_Global_Admin") ||
      originalGroups.includes("DEV_Okta_CDP_Developer");

    const isValidRole = simRole === "marketer" || simRole === "marketer_admin";
    const wantsSim = Boolean(isValidRole && simTags.length > 0 && simCountries.length > 0);

    if (!isAllowedToSimulate || !wantsSim) return next;

    const countriesRes = await fetchCountryList({ ccdp_groups: originalGroups });
    const countriesMap = (countriesRes as any)?.data || {};

    const countryNameToCode = new Map<string, string>();
    for (const [name, details] of Object.entries(countriesMap) as any) {
      const code = details?.countryCode;
      if (typeof name === "string" && typeof code === "string") {
        countryNameToCode.set(name.toLowerCase(), code.toUpperCase());
      }
    }

    const resolvedCountryCodes = simCountries
      .map((name) => countryNameToCode.get(name))
      .filter(Boolean) as string[];

    if (resolvedCountryCodes.length === 0) return next;

    const roleSuffix = normalizeRole(simRoleRaw);
    const simulatedGroups = resolvedCountryCodes.flatMap((code) =>
      simTags.map((tag) => `${code}_${tag}_Okta_CDP_${roleSuffix}`)
    );

    next.data.ccdp_groups = simulatedGroups;
    return next;
  } catch (error: any) {
    if (error.response) {
      const status = error.response.status;
      if (status === 401) {
        console.warn("Error 401: Unauthorized.");
      } else if (status === 500) {
        console.error("Error 500: Internal server error.");
      } else {
        console.error(`Error ${status}:`, error.response.data);
      }
    } else {
      console.error("Network error:", error.message);
    }
    throw error;
  }
};

/**
 * A React Query hook to fetch and manage the authenticated user's state.
 *
 * This hook uses `fetchAppMe` to retrieve user data and handles caching (staleTime, gcTime).
 * It also manages automatic redirection to the login page on 401 Unauthorized errors.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} [params.query={}] - Query parameters passed to `fetchAppMe`.
 * @param {Object} [params.options={}] - Additional React Query options (e.g., `retry`, `onSuccess`, `onError`).
 * @returns {import("@tanstack/react-query").UseQueryResult} The React Query result object containing user data, loading state, and error info.
 */
const useAppMe = ({ query = {}, options }: any = {}) => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const simRole = params.get("simRole") || "";
  const simCountries = params.get("simCountries") || "";
  const simTag = params.get("simTag") || "";
  const queryResult = useQuery({
    queryKey: ["me", query, simRole, simCountries, simTag],
    queryFn: () => fetchAppMe({ query }),
    staleTime: 1000 * 60 * 120,
    gcTime: 1000 * 60 * 120,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 3;
    },
    ...options,
  });

  const { isError, error } = queryResult;

  useEffect(() => {
    if (isError && error) {
      const err = error as any;
      if (err?.response?.status === 401) {
        if (!sessionStorage.getItem("redirectUrl")) {
          sessionStorage.setItem(
            "redirectUrl",
            window.location.pathname + window.location.search
          );
        }
        if (!window.location.pathname.includes("/login")) {
          navigate("/login", { replace: true });
        }
      }
      if (options?.onError) {
        options.onError(err);
      }
    }
  }, [isError, error, navigate, options]);

  return queryResult as any;
};

/**
 * A React Query hook to fetch the list of available countries based on the user's permissions.
 *
 * This hook depends on `useAppMe` to retrieve the user's `ccdp_groups`.
 * It fetches the country list only after the user data is loaded and groups are available.
 * The data is transformed to an array of country objects with `name`, `code`, `admin` status, and `tags`.
 *
 * @returns {import("@tanstack/react-query").UseQueryResult<Array<{name: string, code: string, admin: number, tags: string[]}>>} The React Query result containing the transformed country list.
 */
const useCountryList = () => {
  const { data: meData, isLoading: meLoading } = useAppMe();
  const ccdpGroups = meData?.data?.ccdp_groups || [];

  return useQuery({
    queryKey: ["get_countries", ccdpGroups],
    queryFn: async () => {
      const res = await fetchCountryList({ ccdp_groups: ccdpGroups });
      return res;
    },
    staleTime: 1000 * 60 * 120,
    gcTime: 1000 * 60 * 120,
    enabled: !meLoading && ccdpGroups.length > 0,
    select: (res: { data?: Record<string, { admin: number; countryCode: string; tags: string[] }> }) =>
      Object.entries(res.data || {}).map(([name, details]) => {
        const d = details as { admin: number; countryCode: string; tags: string[] };
        return {
          name,
          code: d.countryCode,
          admin: d.admin,
          tags: d.tags
        };
      }),
  });
};

export { useAppMe, useCountryList };
